/**
 * ARMS Browser RUM 用户关联（对齐 Android/iOS：userName=邮箱，extra userId=customerCode）。
 * SDK 就绪：`index.html` 在 browser-sdk.js onload 后向 window 派发 `rum-sdk-ready`。
 */

/** @type {string} 与 public/index.html 中 dispatchEvent 名称一致 */
export const RUM_SDK_READY_EVENT = 'rum-sdk-ready';

const SDK_READY_RETRY_MS = 50;
const SDK_READY_MAX_RETRIES = 8;

/** 每次 bind/clear 递增，异步回调只执行与当前 epoch 一致的操作 */
let userBindEpoch = 0;
/** 期望绑定的用户 key；clear 时为空 */
let lastBoundKey = '';

function getArmsRumSdk() {
    const sdk = window.RumSDK?.default;
    if (sdk && typeof sdk.setConfig === 'function') {
        return sdk;
    }
    return null;
}

function runWithSdk(run) {
    const sdk = getArmsRumSdk();
    if (sdk) {
        run(sdk);
        return true;
    }
    return false;
}

function whenArmsReady(run) {
    if (runWithSdk(run)) {
        return;
    }

    const retryUntilReady = (attempt) => {
        if (runWithSdk(run)) {
            return;
        }
        if (attempt < SDK_READY_MAX_RETRIES) {
            window.setTimeout(() => retryUntilReady(attempt + 1), SDK_READY_RETRY_MS);
        }
    };

    // onload 已发生但 RumSDK 尚未挂上时，监听会错过事件，直接短重试
    if (window.__rumSdkReady) {
        retryUntilReady(0);
        return;
    }

    window.addEventListener(RUM_SDK_READY_EVENT, () => retryUntilReady(0), { once: true });
}

/**
 * @param {string} [email]
 * @param {string} [customerCode]
 */
export function bindRumUser(email, customerCode) {
    const name = String(email ?? '').trim();
    const code = String(customerCode ?? '').trim();
    if (!name && !code) {
        return;
    }
    const boundKey = `${name}|${code}`;
    if (boundKey === lastBoundKey) {
        return;
    }

    userBindEpoch += 1;
    const epoch = userBindEpoch;

    whenArmsReady((sdk) => {
        if (epoch !== userBindEpoch || boundKey === lastBoundKey) {
            return;
        }
        try {
            sdk.setConfig({
                user: {
                    ...(name ? { name } : {}),
                    ...(code ? { tags: `userId:${code}` } : {}),
                },
            });
            lastBoundKey = boundKey;
        } catch (e) {
            console.debug('bindRumUser failed', e);
        }
    });
}

/** 登出或 token 失效：取消未执行的 bind，并清空 RUM 用户 */
export function clearRumUser() {
    userBindEpoch += 1;
    const epoch = userBindEpoch;
    lastBoundKey = '';

    whenArmsReady((sdk) => {
        if (epoch !== userBindEpoch) {
            return;
        }
        try {
            sdk.setConfig({
                user: {
                    name: '',
                    tags: '',
                },
            });
        } catch (e) {
            console.debug('clearRumUser failed', e);
        }
    });
}
